import React, { useState, useEffect, useCallback, useMemo } from 'react';
import reportingFinancierApi from '../services/reporting-financier';
import useReportingData from '../hooks/useReportingData';
import UpdatingOverlay from './UpdatingOverlay';
import './ReportingPdrApv.css';

// Reporting Financier — onglets PDR / APV.
// Reçoit `type` ('PDR' | 'APV') en prop. Présente un tableau de bord :
//   - Header avec période + filtres
//   - Encart IA (super-admin)
//   - KPI hero cards (CA, factures, ticket moyen, [+ heures/taux horaire APV])
//   - Tableau de comparaison N/N-1 (Mois en cours / Mois N-1 / Cumul année / Cumul N-1)
//   - Ventilations par concession / département / marque / prestation (APV)
//
// Les valeurs sensibles (concessions, départements, prestations) sont
// anonymisées avant envoi à Claude par le backend (cf. RGPD.md §2.12).

// ─── Configuration par segment ────────────────────────────────────────────
// Icônes monochromes inline (currentColor), pas de fond teinté.
const SEGMENT_CONFIG = {
  PDR: {
    title: 'Pièces détachées',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    ),
  },
  APV: {
    title: 'Atelier / Main d\'œuvre',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
      </svg>
    ),
  },
  TRX: {
    title: 'Travaux extérieurs sous-traités',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18"/>
        <path d="M5 21V7l8-4v18"/>
        <path d="M19 21V11l-6-4"/>
        <path d="M9 9v.01"/><path d="M9 12v.01"/><path d="M9 15v.01"/><path d="M9 18v.01"/>
      </svg>
    ),
  },
};

// ─── Helpers de formatage ─────────────────────────────────────────────────
// Formatage sans décimale (pas de virgule) — montants arrondis à l'euro,
// nombres entiers, heures arrondies.
const fmtMoney = (v) => v == null
  ? '—'
  : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const fmtNumber = (v) => v == null ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v);
// 0 h est considéré comme "donnée non disponible" : le sync SFTP actuel ne
// remonte pas la quantité d'heures pour les lignes M.O. (champ CSV non
// identifié). À corriger lors d'une future itération du sync.
const fmtHours  = (v) => (v == null || v === 0) ? '—' : `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v)} h`;

const fmtDelta = (pct) => {
  if (pct == null) return null;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)} %`;
};

const deltaClass = (pct, inverseColor = false) => {
  if (pct == null || pct === 0) return 'rpa-delta-neutral';
  const positive = pct > 0;
  if (inverseColor) return positive ? 'rpa-delta-down' : 'rpa-delta-up';
  return positive ? 'rpa-delta-up' : 'rpa-delta-down';
};

function defaultPeriode() {
  const now = new Date();
  const debut = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    date_debut: debut.toISOString().slice(0, 10),
    date_fin:   now.toISOString().slice(0, 10),
  };
}

// ─── Sous-composants ──────────────────────────────────────────────────────

// Mini badge delta (+12,5 % en vert / -3,2 % en rouge)
function DeltaBadge({ pct }) {
  if (pct == null) return <span className="rpa-delta-empty">—</span>;
  const cls = deltaClass(pct);
  const arrow = pct > 0 ? '▲' : pct < 0 ? '▼' : '◆';
  return (
    <span className={`rpa-delta ${cls}`}>
      <span className="rpa-delta-arrow">{arrow}</span>
      {fmtDelta(pct)}
    </span>
  );
}

// Carte KPI plate : label · valeur · delta + sublabel inline.
// Pas d'icône, pas de stripe colorée — la sobriété passe par la typo.
function KpiHero({ label, value, valueN1, sublabel, delta }) {
  return (
    <div className="rpa-hero-card">
      <div className="rpa-hero-label">{label}</div>
      <div className="rpa-hero-value">{value}</div>
      <div className="rpa-hero-foot">
        {delta != null
          ? <DeltaBadge pct={delta} />
          : <span className="rpa-delta-empty">—</span>
        }
        <span className="rpa-hero-sublabel">
          {valueN1 != null
            ? <>vs <strong>{valueN1}</strong> (N-1)</>
            : (sublabel || 'vs N-1')}
        </span>
      </div>
    </div>
  );
}

// Format monétaire compact pour les étiquettes de barres (110 112 € → « 110 k€ »).
const fmtMoneyK = (v) => v == null
  ? '—'
  : (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)} k€` : `${Math.round(v)} €`);

// Graphe d'évolution mensuelle du CA / jour ouvré (barres tokenisées, sans lib).
// Même grammaire visuelle que l'EvolutionChart VN, en mono-série, avec une
// ligne médiane de référence. SCALE laisse une marge haute pour les étiquettes.
const EVO_SCALE = 0.88;
function EvolutionCaJourChart({ points }) {
  if (!points || points.length === 0) {
    return <div className="rpa-empty">Aucune donnée sur la période.</div>;
  }
  const vals = points.map(p => p.ca_par_jour_ouvre || 0);
  const maxV = Math.max(...vals, 1);
  // Médiane des valeurs affichées (mois de la période sélectionnée).
  const sorted = [...vals].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  const medPct = (median / maxV) * 100 * EVO_SCALE;
  const fmtMonth = (b) => {
    const [y, m] = b.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, 1)
      .toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
  };
  return (
    <div className="rpa-evo">
      <div className="rpa-evo-scroll">
        <div className="rpa-evo-plot">
          {points.map((p, i) => {
            const h = Math.max(((p.ca_par_jour_ouvre || 0) / maxV) * 100 * EVO_SCALE, 1);
            return (
              <div
                className="rpa-evo-col"
                key={i}
                title={`${fmtMonth(p.bucket)} · CA/jour ${fmtMoney(p.ca_par_jour_ouvre)} · CA ${fmtMoney(p.ca)} · ${p.jours_ouvres} jour(s) ouvré(s)`}
              >
                <div className="rpa-evo-bar" style={{ height: `${h}%` }}>
                  <span className="rpa-evo-val">{fmtMoneyK(p.ca_par_jour_ouvre)}</span>
                </div>
              </div>
            );
          })}
          <div className="rpa-evo-median" style={{ bottom: `${medPct}%` }} title={`Médiane : ${fmtMoney(median)} / jour ouvré`}>
            <span className="rpa-evo-median-label">Médiane&nbsp;{fmtMoneyK(median)}</span>
          </div>
        </div>
        <div className="rpa-evo-xaxis">
          {points.map((p, i) => (
            <span className="rpa-evo-xlabel" key={i}>{fmtMonth(p.bucket)}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Tableau de comparaison N / N-1
function ComparisonTable({ type, comparison, budget }) {
  if (!comparison) return null;
  const c = comparison;
  const bm = budget?.mois_budget;
  const bc = budget?.cumul_budget;
  const hasBudget = bm && bc && (bm.ca > 0 || bc.ca > 0);

  const baseRows = [
    { key: 'ca',           label: 'CA HT',        format: fmtMoney },
    { key: 'nb_factures',  label: 'Factures',      format: fmtNumber, noBudget: true },
    { key: 'ticket_moyen', label: 'Ticket moyen',  format: fmtMoney, noBudget: true },
  ];
  if (type === 'APV') {
    baseRows.push({ key: 'heures',       label: 'Heures',      format: fmtHours });
    baseRows.push({ key: 'taux_horaire', label: 'Taux horaire', format: (v) => v ? fmtMoney(v) + '/h' : '—', noBudget: true });
  }

  const budgetDelta = (real, bgt) => {
    if (bgt == null || bgt === 0 || real == null) return null;
    return Math.round(((real - bgt) / bgt) * 10000) / 100;
  };

  const renderSubHeaders = () => (
    <>
      <th>Réalisé</th>
      <th>N-1</th>
      <th>Δ</th>
      {hasBudget && <><th className="rpa-comp-th-budget">Budget</th><th className="rpa-comp-th-budget">Δ Bgt</th></>}
    </>
  );

  return (
    <div className="rpa-comparison-block">
      <div className="rpa-block-header">
        <h3>Comparaison N / N-1 {hasBudget ? '/ Budget' : ''}</h3>
        <span className="rpa-block-sub">Ancrage : {new Date(c.anchor_date).toLocaleDateString('fr-FR')}</span>
      </div>
      <div className="rpa-comparison-table-wrap">
        <table className="rpa-comparison-table rpa-comp-split">
          <thead>
            <tr>
              <th rowSpan={2} className="rpa-comp-th-kpi"></th>
              <th colSpan={hasBudget ? 5 : 3} className="rpa-comp-th-group rpa-comp-group-mois">📅 Mois en cours</th>
              <th colSpan={hasBudget ? 5 : 3} className="rpa-comp-th-group rpa-comp-group-cumul">📊 Cumul année</th>
            </tr>
            <tr>{renderSubHeaders()}{renderSubHeaders()}</tr>
          </thead>
          <tbody>
            {baseRows.map(row => (
              <tr key={row.key}>
                <td className="rpa-comp-td-label">{row.label}</td>
                {/* Mois en cours */}
                <td className="rpa-comp-td-value rpa-comp-first">{row.format(c.mois_courant[row.key])}</td>
                <td className="rpa-comp-td-value rpa-comp-td-prev">{row.format(c.mois_n1[row.key])}</td>
                <td className="rpa-comp-td-delta"><DeltaBadge pct={c.deltas.mois[row.key]} /></td>
                {hasBudget && !row.noBudget && (
                  <>
                    <td className="rpa-comp-td-value rpa-comp-td-budget">{row.format(bm[row.key])}</td>
                    <td className="rpa-comp-td-delta"><DeltaBadge pct={budgetDelta(c.mois_courant[row.key], bm[row.key])} /></td>
                  </>
                )}
                {hasBudget && row.noBudget && <><td className="rpa-comp-td-budget">—</td><td>—</td></>}
                {/* Cumul année */}
                <td className="rpa-comp-td-value rpa-comp-first">{row.format(c.cumul_annee[row.key])}</td>
                <td className="rpa-comp-td-value rpa-comp-td-prev">{row.format(c.cumul_n1[row.key])}</td>
                <td className="rpa-comp-td-delta"><DeltaBadge pct={c.deltas.cumul[row.key]} /></td>
                {hasBudget && !row.noBudget && (
                  <>
                    <td className="rpa-comp-td-value rpa-comp-td-budget">{row.format(bc[row.key])}</td>
                    <td className="rpa-comp-td-delta"><DeltaBadge pct={budgetDelta(c.cumul_annee[row.key], bc[row.key])} /></td>
                  </>
                )}
                {hasBudget && row.noBudget && <><td className="rpa-comp-td-budget">—</td><td>—</td></>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Modale Référentiel des départements ──────────────────────────────────
// Affecte un libellé personnalisé à chaque code brut de `factures_services.department`.
// Plusieurs codes bruts portant le même libellé sont fusionnés dans les ventilations
// (ex. « M1-Magasin », « M1-MAGASIN », « M1-60-MAGASIN PR » → « Comptoir »).
function ConfigDepartementsModal({ onClose, onSaved }) {
  const [rows, setRows]       = useState([]);     // [{code_brut, nb_factures, libelle}]
  const [labels, setLabels]   = useState({});     // code_brut -> libellé édité
  const [presets, setPresets] = useState([]);     // libellés distincts (autocomplétion)
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    reportingFinancierApi.getPdrDepartements()
      .then(r => {
        const deps = r.data?.departements || [];
        setRows(deps);
        const init = {};
        deps.forEach(d => { init[d.code_brut] = d.libelle || ''; });
        setLabels(init);
        setPresets(r.data?.libelles_distincts || []);
      })
      .catch(e => setError(e.response?.data?.error || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, []);

  const setLabel = (code, val) => setLabels(prev => ({ ...prev, [code]: val }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const items = rows.map(r => ({ code_brut: r.code_brut, libelle: (labels[r.code_brut] || '').trim() }));
      await reportingFinancierApi.savePdrDepartements(items);
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur à l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Libellés actuellement saisis (en plus des presets) pour l'autocomplétion.
  const datalistOptions = useMemo(() => {
    const set = new Set(presets);
    Object.values(labels).forEach(v => { if (v && v.trim()) set.add(v.trim()); });
    return [...set].sort();
  }, [presets, labels]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.code_brut.toLowerCase().includes(q) ||
      (labels[r.code_brut] || '').toLowerCase().includes(q));
  }, [rows, labels, search]);

  const nbMapped = useMemo(
    () => rows.filter(r => (labels[r.code_brut] || '').trim()).length,
    [rows, labels]);

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚙️ Référentiel des départements</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p className="rpa-ref-help">
            Affectez un nom personnalisé à chaque département. Plusieurs codes bruts
            portant le <strong>même libellé</strong> sont fusionnés dans les indicateurs
            (ex.&nbsp;: <em>M1-Magasin</em>, <em>M1-MAGASIN</em> → <strong>Comptoir</strong>).
            Laissez vide pour conserver le code d'origine.
          </p>
          <input
            className="page-search"
            placeholder="🔍 Rechercher un département…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', marginBottom: 12 }}
          />
          {error && <div className="rpa-error">⚠️ {error}</div>}
          {loading ? (
            <div className="rpa-loading">Chargement…</div>
          ) : (
            <table className="rpa-ref-table">
              <thead>
                <tr>
                  <th>Code d'origine</th>
                  <th style={{ textAlign: 'right' }}>Factures</th>
                  <th>Libellé personnalisé</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(r => (
                  <tr key={r.code_brut}>
                    <td className="rpa-ref-code">{r.code_brut}</td>
                    <td style={{ textAlign: 'right' }}>{fmtNumber(r.nb_factures)}</td>
                    <td>
                      <input
                        className="rpa-ref-input"
                        list="rpa-ref-libelles"
                        placeholder={r.code_brut}
                        value={labels[r.code_brut] || ''}
                        onChange={e => setLabel(r.code_brut, e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <datalist id="rpa-ref-libelles">
            {datalistOptions.map(o => <option key={o} value={o} />)}
          </datalist>
        </div>
        <div className="modal-footer">
          <span className="rpa-ref-foot-info">{nbMapped} / {rows.length} départements personnalisés</span>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bloc « Détail » (onglets) — commence par « Par mois » ──────────────────
// Réutilise les classes .rvn-table / .mar-monthly-toggle chargées globalement
// par le shell Reporting (imports eager de ReportingVN/ReportingMarche).
function ApvMonthlyTable({ points }) {
  if (!points || points.length === 0) return <div className="rpa-empty">Aucune donnée sur la période.</div>;
  const fmtM = (b) => {
    const [y, m] = String(b).split('-');
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
  };
  const rows = [...points].sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)));
  return (
    <div className="rvn-table-wrap">
      <table className="rvn-table">
        <thead>
          <tr>
            <th>Mois</th>
            <th className="rvn-num">CA HT</th>
            <th className="rvn-num">CA / jour ouvré</th>
            <th className="rvn-num">Nb factures</th>
            <th className="rvn-num">Ticket moyen</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={i}>
              <td>{fmtM(p.bucket)}</td>
              <td className="rvn-num">{fmtMoney(p.ca)}</td>
              <td className="rvn-num">{fmtMoney(p.ca_par_jour_ouvre)}</td>
              <td className="rvn-num">{fmtNumber(p.nb_factures)}</td>
              <td className="rvn-num">{fmtMoney(p.ticket_moyen)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Tableau d'une dimension (marque / concession / prestation) — mêmes données
// que « Par mois » (CA · Nb factures · Ticket moyen). Ticket = CA ÷ nb factures.
function ApvDimTable({ rows, entityLabel }) {
  if (!rows || rows.length === 0) return <div className="rpa-empty">Aucune donnée sur la période.</div>;
  return (
    <div className="rvn-table-wrap">
      <table className="rvn-table">
        <thead>
          <tr>
            <th>{entityLabel}</th>
            <th className="rvn-num">CA HT</th>
            <th className="rvn-num">Nb factures</th>
            <th className="rvn-num">Ticket moyen</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const ticket = r.ticket_moyen != null ? r.ticket_moyen : (r.nb_factures ? r.ca / r.nb_factures : null);
            return (
              <tr key={i}>
                <td>{r.label}</td>
                <td className="rvn-num">{fmtMoney(r.ca)}</td>
                <td className="rvn-num">{fmtNumber(r.nb_factures)}</td>
                <td className="rvn-num">{fmtMoney(ticket)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ApvDetailTabs({ kpis }) {
  const avail = {
    mois:        (kpis.evolution || []).length > 0,
    marque:      (kpis.par_marque || []).length > 0,
    concession:  (kpis.par_concession || []).length > 0,
    prestations: (kpis.par_prestation || []).length > 0,
  };
  const [tab, setTab] = useState('mois');
  const active = avail[tab] ? tab : 'mois';
  const n = (arr) => (kpis[arr] || []).length;
  const countSub =
    active === 'mois'        ? `${n('evolution')} mois`
    : active === 'marque'    ? `${n('par_marque')} marque${n('par_marque') > 1 ? 's' : ''}`
    : active === 'concession' ? `${n('par_concession')} concession${n('par_concession') > 1 ? 's' : ''}`
    : `${n('par_prestation')} prestation${n('par_prestation') > 1 ? 's' : ''}`;
  return (
    <div className="rpa-dim-block">
      <div className="rpa-block-header">
        <h3>Détail</h3>
        <span className="rpa-block-sub">{countSub}</span>
      </div>
      <div className="mar-monthly-toggle">
        {avail.mois &&        <button className={active === 'mois' ? 'active' : ''} onClick={() => setTab('mois')}>Par mois</button>}
        {avail.marque &&      <button className={active === 'marque' ? 'active' : ''} onClick={() => setTab('marque')}>Par marque</button>}
        {avail.concession &&  <button className={active === 'concession' ? 'active' : ''} onClick={() => setTab('concession')}>Par concession</button>}
        {avail.prestations && <button className={active === 'prestations' ? 'active' : ''} onClick={() => setTab('prestations')}>Par prestations</button>}
      </div>
      {active === 'mois' &&        <ApvMonthlyTable points={kpis.evolution || []} />}
      {active === 'marque' &&      <ApvDimTable rows={kpis.par_marque} entityLabel="Marque" />}
      {active === 'concession' &&  <ApvDimTable rows={kpis.par_concession} entityLabel="Concession" />}
      {active === 'prestations' && <ApvDimTable rows={kpis.par_prestation} entityLabel="Prestation" />}
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────
// Reçoit les filtres période + concessions depuis le shell (props). Les
// filtres département + marque restent locaux car ils sont spécifiques au
// segment courant.
function ReportingPdrApv({
  type, user, setHeaderAction,
  dateDebut, dateFin, concessions,
}) {
  const cfg = SEGMENT_CONFIG[type];
  // Fallback période si pas de shell (utilisation directe / tests).
  // `effConcessions` mémorisé pour ne pas créer une nouvelle réf à chaque render
  // (sinon useMemo en aval se redéclenche en boucle).
  const def = useMemo(() => defaultPeriode(), []);
  const effDateDebut = dateDebut || def.date_debut;
  const effDateFin   = dateFin   || def.date_fin;
  const effConcessions = useMemo(() => concessions || [], [concessions]);

  const [department, setDepartment] = useState('');
  const [marque, setMarque]         = useState('');
  const [dimensions, setDimensions] = useState({ concessions: [], departments: [], marques: [] });

  const [analyse, setAnalyse]               = useState(null);
  const [analyseLoading, setAnalyseLoading] = useState(false);
  const [analyseError, setAnalyseError]     = useState(null);
  const [iaHistory, setIaHistory]           = useState([]);
  const [showRefModal, setShowRefModal]     = useState(false);

  // Charger les valeurs des selects au mount
  useEffect(() => {
    reportingFinancierApi.getDimensions(type)
      .then(r => setDimensions(r.data || { concessions: [], departments: [], marques: [] }))
      .catch(() => {});
    reportingFinancierApi.getAnalyseIaHistory(type, 10)
      .then(r => setIaHistory(r.data?.analyses || []))
      .catch(() => {});
    return () => { if (setHeaderAction) setHeaderAction(null); };
  }, [type, setHeaderAction]);

  const filters = useMemo(() => ({
    date_debut: effDateDebut,
    date_fin:   effDateFin,
    ...(effConcessions.length ? { concession: effConcessions } : {}),
    ...(department ? { department } : {}),
    ...(marque     ? { marque     } : {}),
  }), [effDateDebut, effDateFin, effConcessions, department, marque]);

  const compFilters = useMemo(() => ({
    date_fin: effDateFin,
    ...(effConcessions.length ? { concession: effConcessions } : {}),
    ...(department ? { department } : {}),
    ...(marque     ? { marque     } : {}),
  }), [effDateFin, effConcessions, department, marque]);

  const { data, loading, error, reload } = useReportingData(
    (signal) => Promise.all([
      reportingFinancierApi.getKpis(type, filters, signal),
      reportingFinancierApi.getComparison(type, compFilters, signal),
      reportingFinancierApi.getBudgetComparison({ service: type, date_fin: effDateFin, ...(effConcessions.length ? { concession: effConcessions } : {}) }, signal).catch(() => null),
    ]).then(([k, c, b]) => ({ kpis: k.data, comparison: c.data, budgetComp: b?.data || null })),
    [type, effDateDebut, effDateFin, JSON.stringify(effConcessions), department, marque],
    { enabled: !!(effDateDebut && effDateFin) },
  );
  const kpis       = data?.kpis || null;
  const comparison = data?.comparison || null;
  const budgetComp = data?.budgetComp || null;

  // Recharge la liste des départements (dropdown) + les KPIs après édition du référentiel.
  const handleRefSaved = useCallback(() => {
    reportingFinancierApi.getDimensions(type)
      .then(r => setDimensions(r.data || { concessions: [], departments: [], marques: [] }))
      .catch(() => {});
    reload();
  }, [type, reload]);

  const handleRunAnalyse = async (force = false) => {
    setAnalyseLoading(true);
    setAnalyseError(null);
    try {
      const r = await reportingFinancierApi.runAnalyseIa(type, {
        date_debut: effDateDebut, date_fin: effDateFin,
        filtres: {
          ...(effConcessions.length ? { concession: effConcessions } : {}),
          ...(department ? { department } : {}),
          ...(marque ? { marque } : {}),
        },
        force,
      });
      setAnalyse(r.data);
      reportingFinancierApi.getAnalyseIaHistory(type, 10)
        .then(rr => setIaHistory(rr.data?.analyses || []))
        .catch(() => {});
    } catch (e) {
      setAnalyseError(e.response?.data?.error || 'Erreur d\'analyse IA');
    } finally {
      setAnalyseLoading(false);
    }
  };

  const isSuperAdmin = user?.role === 'super-admin';

  // ─── Rendu : KPI hero cards ─────────────────────────────────────────────
  // Hero cards (CA, Factures, Ticket moyen, +Heures et Taux horaire APV).
  const renderHeroCards = () => {
    if (!kpis) return null;
    const g = kpis.kpis_globaux || {};
    return (
      <div className="rpa-hero-grid">
        <KpiHero
          label="Chiffre d'affaires HT"
          value={fmtMoney(g.ca)}
          valueN1={g.ca_n1 != null ? fmtMoney(g.ca_n1) : null}
          delta={g.delta_ca_pct}
        />
        <KpiHero
          label="Factures"
          value={fmtNumber(g.nb_factures)}
          valueN1={g.nb_factures_n1 != null ? fmtNumber(g.nb_factures_n1) : null}
          delta={g.delta_nb_factures_pct}
        />
        <KpiHero
          label="Ticket moyen"
          value={fmtMoney(g.ticket_moyen)}
          valueN1={g.ticket_moyen_n1 != null ? fmtMoney(g.ticket_moyen_n1) : null}
          delta={g.delta_ticket_moyen_pct}
        />
        {type === 'APV' && (
          <>
            <KpiHero
              label="Heures facturées"
              value={fmtHours(g.heures)}
              valueN1={(g.heures_n1 != null && g.heures_n1 > 0) ? fmtHours(g.heures_n1) : null}
              delta={g.delta_heures_pct}
            />
            <KpiHero
              label="Taux horaire moyen"
              value={g.taux_horaire ? fmtMoney(g.taux_horaire) + '/h' : '—'}
              valueN1={(g.taux_horaire_n1 != null && g.taux_horaire_n1 > 0) ? fmtMoney(g.taux_horaire_n1) + '/h' : null}
              delta={g.delta_taux_horaire_pct}
            />
          </>
        )}
        {(type === 'APV' || type === 'PDR') && (
          <KpiHero
            label="CA / jour ouvré"
            value={g.ca_par_jour_ouvre != null ? fmtMoney(g.ca_par_jour_ouvre) : '—'}
            valueN1={(g.ca_par_jour_ouvre_n1 != null && g.ca_par_jour_ouvre_n1 > 0) ? fmtMoney(g.ca_par_jour_ouvre_n1) : null}
            delta={g.delta_ca_par_jour_ouvre_pct}
            sublabel={g.jours_ouvres != null ? `${g.jours_ouvres} jour(s) ouvré(s)` : null}
          />
        )}
      </div>
    );
  };

  // ─── Rendu : encart IA ──────────────────────────────────────────────────
  const renderAnalyse = () => {
    if (!analyse) return null;
    const r = analyse.resultat || {};
    return (
      <div className="rpa-ia-result">
        <div className="rpa-ia-head">
          <span className="rpa-ia-emoji">🤖</span>
          <h3>Analyse IA — {cfg.title}</h3>
          {analyse.cached && (
            <span className="rpa-ia-cached-tag">
              cache · {new Date(analyse.created_at).toLocaleString('fr-FR')}
              <button className="rpa-ia-refresh" onClick={() => handleRunAnalyse(true)}>↻ Relancer</button>
            </span>
          )}
        </div>
        <div className="rpa-ia-body">
          <div className="rpa-ia-synthese">
            <p>{r.synthese || '—'}</p>
            {typeof r.score_risque === 'number' && (
              <div className={`rpa-ia-score rpa-ia-score-${r.score_risque > 70 ? 'high' : r.score_risque > 40 ? 'mid' : 'low'}`}>
                <span>Score de risque</span>
                <strong>{r.score_risque}/100</strong>
              </div>
            )}
          </div>
          {Array.isArray(r.anomalies) && r.anomalies.length > 0 && (
            <div className="rpa-ia-anomalies">
              <h4>Anomalies détectées</h4>
              <ul>
                {r.anomalies.map((a, i) => (
                  <li key={i} className={`rpa-ia-gravite-${a.gravite || 'orange'}`}>
                    <div className="rpa-ia-anom-head">
                      <span className="rpa-ia-anom-dot"></span>
                      <strong>{a.titre}</strong>
                    </div>
                    <div className="rpa-ia-details">{a.details}</div>
                    {a.ref && <div className="rpa-ia-ref">{a.ref}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(r.recommandations) && r.recommandations.length > 0 && (
            <div className="rpa-ia-reco">
              <h4>Recommandations</h4>
              <ul>{r.recommandations.map((rec, i) => <li key={i}>{rec}</li>)}</ul>
            </div>
          )}
        </div>
        {analyse.cout_estime_usd != null && (
          <div className="rpa-ia-meta">
            {analyse.modele} · {analyse.tokens_in} in / {analyse.tokens_out} out · ≈ {(analyse.cout_estime_usd * 0.93).toFixed(3)} €
          </div>
        )}
      </div>
    );
  };

  // ─── Rendu principal ────────────────────────────────────────────────────
  return (
    <div className="rpa-dashboard rf-surface">
      <UpdatingOverlay show={loading} />

      {/* Header — titre segment + filtres locaux (dept / marque / IA) */}
      <div className="rpa-dashboard-header">
        <div className="rpa-dashboard-title">
          <span className="rpa-dashboard-icon">{cfg.icon}</span>
          <div>
            <h2>{cfg.title}</h2>
            <div className="rpa-dashboard-subtitle">
              Du {new Date(effDateDebut).toLocaleDateString('fr-FR')} au {new Date(effDateFin).toLocaleDateString('fr-FR')}
            </div>
          </div>
        </div>
        <div className="rpa-toolbar">
          <select value={department} onChange={e => setDepartment(e.target.value)} className={`page-select ${department ? 'active-filter' : ''}`}>
            <option value="">Tous départements</option>
            {dimensions.departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={marque} onChange={e => setMarque(e.target.value)} className={`page-select ${marque ? 'active-filter' : ''}`}>
            <option value="">Toutes marques</option>
            {dimensions.marques.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          {isSuperAdmin && type === 'PDR' && (
            <button
              className="rpa-ref-btn"
              onClick={() => setShowRefModal(true)}
              title="Normaliser les libellés de départements"
            >
              ⚙️ Référentiel
            </button>
          )}
          {isSuperAdmin && (
            <button
              className="rpa-ia-btn"
              onClick={() => handleRunAnalyse(false)}
              disabled={analyseLoading}
              title="Lancer une analyse IA des dérives"
            >
              {analyseLoading ? '⏳' : '🤖'} {analyseLoading ? 'Analyse en cours…' : 'Analyse IA'}
            </button>
          )}
        </div>
      </div>

      {/* Erreurs */}
      {error && !kpis && <div className="rpa-error">⚠️ {error}</div>}
      {analyseError && <div className="rpa-error">⚠️ {analyseError}</div>}

      {/* Encart IA */}
      {analyse && renderAnalyse()}

      {/* KPI hero cards */}
      {!kpis
        ? <div className="rpa-loading">Chargement…</div>
        : renderHeroCards()
      }

      {/* Tableau de comparaison N/N-1 */}
      {comparison && <ComparisonTable type={type} comparison={comparison} budget={budgetComp} />}

      {/* Évolution mensuelle du CA / jour ouvré (APV / PDR) */}
      {kpis &&(type === 'APV' || type === 'PDR') && Array.isArray(kpis.evolution) && kpis.evolution.length > 0 && (
        <div className="rpa-evo-block">
          <div className="rpa-block-header"><h3>Évolution du CA / jour ouvré</h3></div>
          <EvolutionCaJourChart points={kpis.evolution} />
        </div>
      )}

      {/* Bloc Détail (onglets) — commence par « Par mois » */}
      {kpis && Array.isArray(kpis.evolution) && kpis.evolution.length > 0 && (
        <ApvDetailTabs kpis={kpis} />
      )}


      {/* Historique IA */}
      {isSuperAdmin && iaHistory.length > 0 && (
        <div className="rpa-ia-history">
          <h4>Historique des analyses IA</h4>
          <ul>
            {iaHistory.map(h => (
              <li key={h.id}>
                <span className="rpa-ia-history-date">{new Date(h.created_at).toLocaleString('fr-FR')}</span>
                {' — '}
                <span className="rpa-ia-history-periode">{h.periode_debut} → {h.periode_fin}</span>
                {h.cout_estime_usd != null && (
                  <span className="rpa-ia-history-cout"> · ≈ {(parseFloat(h.cout_estime_usd) * 0.93).toFixed(3)} €</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Modale Référentiel des départements */}
      {showRefModal && (
        <ConfigDepartementsModal
          onClose={() => setShowRefModal(false)}
          onSaved={handleRefSaved}
        />
      )}
    </div>
  );
}

export default ReportingPdrApv;
